import { useEditor, Editor } from '@tiptap/react';
import { Extension } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Mention from '@tiptap/extension-mention';
import { Placeholder } from '@tiptap/extensions';

import { commentMentionSuggestion, COMMENT_MENTION_PLUGIN_KEY } from './suggestion';
import { parseCommentContent } from './content';

interface UseCommentEditorOptions {
  mentionsEnabled: boolean;
  placeholder?: string;
  initialContent?: string;
  editable?: boolean;
  onEnter?: () => void;
}

const SUBMIT_SHORTCUT_PRIORITY = 1000;

function createSubmitShortcut(onEnter: () => void): Extension {
  return Extension.create({
    name: 'commentSubmitShortcut',
    priority: SUBMIT_SHORTCUT_PRIORITY,
    addKeyboardShortcuts() {
      return {
        Enter: ({ editor }) => {
          if (COMMENT_MENTION_PLUGIN_KEY.getState(editor.state)?.active) {
            return false;
          }
          onEnter();
          return true;
        },
      };
    },
  });
}

export function useCommentEditor({
  mentionsEnabled,
  placeholder,
  initialContent,
  editable = true,
  onEnter,
}: UseCommentEditorOptions): Editor | null {
  return useEditor(
    {
      editable,
      immediatelyRender: false,
      extensions: [
        StarterKit.configure({
          heading: false,
          codeBlock: false,
          horizontalRule: false,
          blockquote: false,
        }),
        ...(mentionsEnabled
          ? [
              Mention.configure({
                HTMLAttributes: { class: 'mention' },
                deleteTriggerWithBackspace: true,
                ...(editable ? { suggestion: commentMentionSuggestion } : {}),
              }),
            ]
          : []),
        ...(onEnter ? [createSubmitShortcut(onEnter)] : []),
        ...(placeholder ? [Placeholder.configure({ placeholder })] : []),
      ],
      content: initialContent ? parseCommentContent(initialContent) : '',
    },
    [mentionsEnabled, editable]
  );
}
